import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiZoomIn, FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { getApiUrl } from '../../utils/apiUrl';

const API_URL = getApiUrl();

// Composant pour afficher un modèle format paysage (horizontal)
function PaysageCard({ model, onClick }) {
  return (
    <div 
      className="w-56 md:w-64 rounded-xl overflow-hidden shadow-lg bg-white cursor-pointer group transition-all duration-300 hover:scale-105 hover:shadow-2xl flex-shrink-0"
      onClick={onClick}
      data-testid={`paysage-card-${model.name?.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className="relative aspect-[3/2]">
        <img
          src={model.url}
          alt={model.name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
              <FiZoomIn className="w-6 h-6 text-gray-800" />
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
          <p className="text-white text-sm font-medium text-center truncate">{model.name}</p>
        </div>
      </div>
    </div>
  );
}

// Composant pour afficher un modèle format bandelette (5x15 cm - très vertical)
function BandeletteCard({ model, onClick }) {
  return (
    <div 
      className="w-24 md:w-28 rounded-xl overflow-hidden shadow-lg bg-white cursor-pointer group transition-all duration-300 hover:scale-105 hover:shadow-2xl flex-shrink-0"
      onClick={onClick}
      data-testid={`bandelette-card-${model.name?.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className="relative aspect-[1/3]">
        <img
          src={model.url}
          alt={model.name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
              <FiZoomIn className="w-5 h-5 text-gray-800" />
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-1 bg-gradient-to-t from-black/80 to-transparent">
          <p className="text-white text-xs font-medium text-center truncate">{model.name}</p>
        </div>
      </div>
    </div>
  );
}

// Composant pour afficher un modèle format portrait (vertical 2:3)
function PortraitCard({ model, onClick }) {
  return (
    <div 
      className="w-40 md:w-48 rounded-xl overflow-hidden shadow-lg bg-white cursor-pointer group transition-all duration-300 hover:scale-105 hover:shadow-2xl flex-shrink-0"
      onClick={onClick}
      data-testid={`portrait-card-${model.name?.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className="relative aspect-[2/3]">
        <img
          src={model.url}
          alt={model.name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
              <FiZoomIn className="w-6 h-6 text-gray-800" />
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
          <p className="text-white text-sm font-medium text-center truncate">{model.name}</p>
        </div>
      </div>
    </div>
  );
}

// Composant Carousel mobile - un modèle à la fois avec flèches
function MobileCarousel({ models, CardComponent, index, setIndex, onCardClick }) {
  if (!models || models.length === 0) return null;
  
  const currentModel = models[index] || models[0];
  const totalModels = models.length;
  
  // Only show dots if there are 10 or fewer models, otherwise just show counter
  const showDots = totalModels <= 10;
  
  return (
    <div className="relative px-4">
      {/* Navigation arrows */}
      <button 
        onClick={() => setIndex(prev => prev === 0 ? models.length - 1 : prev - 1)}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-all"
        data-testid="mobile-carousel-prev"
      >
        <FiChevronLeft className="w-6 h-6" />
      </button>
      <button 
        onClick={() => setIndex(prev => (prev + 1) % models.length)}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-all"
        data-testid="mobile-carousel-next"
      >
        <FiChevronRight className="w-6 h-6" />
      </button>
      
      {/* Current model */}
      <div className="flex justify-center mx-12">
        <CardComponent model={currentModel} onClick={() => onCardClick(currentModel)} />
      </div>
      
      {/* Progress bar instead of dots for many items */}
      {!showDots && (
        <div className="mt-4 mx-auto max-w-xs">
          <div className="h-1 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-amber-500 rounded-full transition-all duration-300"
              style={{ width: `${((index + 1) / totalModels) * 100}%` }}
            />
          </div>
        </div>
      )}
      
      {/* Dots indicator - only for 10 or fewer items */}
      {showDots && (
        <div className="flex justify-center gap-1.5 mt-4">
          {models.map((_, idx) => (
            <button 
              key={idx}
              onClick={() => setIndex(idx)}
              className={`w-2 h-2 rounded-full transition-all ${idx === index ? 'bg-amber-500 w-4' : 'bg-white/30'}`}
            />
          ))}
        </div>
      )}
      
      {/* Counter */}
      <p className="text-center text-white/50 text-sm mt-2">{index + 1} / {totalModels}</p>
    </div>
  );
}

// Composant carrousel avec défilement automatique infini ET navigation manuelle
function InfiniteCarousel({ children, direction = 'left', speed = 30, className = "", gradientColor = "white" }) {
  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  
  // Dupliquer les enfants pour créer un effet infini
  const duplicatedChildren = [...React.Children.toArray(children), ...React.Children.toArray(children)];
  
  // Fonction pour scroll manuel
  const handleManualScroll = (dir) => {
    if (containerRef.current) {
      const scrollAmount = dir === 'left' ? -500 : 500;
      containerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };
  
  return (
    <div className={`relative ${className}`}>
      {/* Flèche gauche */}
      <button
        onClick={() => handleManualScroll('left')}
        className="absolute -left-4 md:-left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 md:w-14 md:h-14 bg-black/80 hover:bg-black rounded-full shadow-xl flex items-center justify-center transition-all duration-200 hover:scale-110"
        data-testid="carousel-prev-btn"
      >
        <FiChevronLeft className="w-6 h-6 md:w-7 md:h-7 text-white" />
      </button>
      
      {/* Flèche droite */}
      <button
        onClick={() => handleManualScroll('right')}
        className="absolute -right-4 md:-right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 md:w-14 md:h-14 bg-black/80 hover:bg-black rounded-full shadow-xl flex items-center justify-center transition-all duration-200 hover:scale-110"
        data-testid="carousel-next-btn"
      >
        <FiChevronRight className="w-6 h-6 md:w-7 md:h-7 text-white" />
      </button>
      
      {/* Container du carrousel avec dégradés */}
      <div 
        ref={containerRef}
        className="overflow-x-auto mx-8 md:mx-10 relative scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Dégradé gauche */}
        <div 
          className="absolute left-0 top-0 bottom-0 w-16 md:w-24 z-10 pointer-events-none"
          style={{ background: `linear-gradient(to right, ${gradientColor}, transparent)` }}
        />
        
        {/* Dégradé droite */}
        <div 
          className="absolute right-0 top-0 bottom-0 w-16 md:w-24 z-10 pointer-events-none"
          style={{ background: `linear-gradient(to left, ${gradientColor}, transparent)` }}
        />
        
        <div
          ref={scrollRef}
          className="flex gap-4"
          style={{
            animation: `scroll-${direction} ${speed}s linear infinite`,
            animationPlayState: isPaused ? 'paused' : 'running',
          }}
        >
          {duplicatedChildren}
        </div>
      </div>
      
      <style>{`
        @keyframes scroll-left {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes scroll-right {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}

export default function ModelesPage({ serviceType = 'photobooth' }) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [paysageModels, setPaysageModels] = useState([]);
  const [portraitModels, setPortraitModels] = useState([]);
  const [bandeletteModels, setBandeletteModels] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Mobile carousel indices
  const [paysageIndex, setPaysageIndex] = useState(0);
  const [portraitIndex, setPortraitIndex] = useState(0);
  const [bandeletteIndex, setBandeletteIndex] = useState(0);

  // Charger les modèles depuis l'API
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/photo-models`);
        const data = await response.json();
        const models = data.models || [];
        
        // Séparer par format
        setPaysageModels(models.filter(m => m.format === 'paysage'));
        setPortraitModels(models.filter(m => m.format === 'portrait'));
        setBandeletteModels(models.filter(m => m.format === 'bandelette'));
      } catch (error) {
        console.error('Erreur lors du chargement des modèles:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchModels();
  }, []);

  // Déterminer le titre en fonction du service
  const getTitle = () => {
    switch(serviceType) {
      case 'videobooth-360':
        return 'Nos Modèles VideoBooth 360°';
      case 'livre-dor':
        return "Nos Modèles Livre d'Or";
      default:
        return 'Nos Modèles PhotoBooth';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/70">Chargement des modèles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="modeles-page">
      {/* Header Section */}
      <div className="bg-gradient-to-b from-gray-900 to-black py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4" data-testid="modeles-title">
            {serviceType === 'livre-dor' ? (
              <>Nos Modèles <span className="gold-shimmer-text">Livre d'Or</span></>
            ) : serviceType === 'videobooth-360' ? (
              <>Nos Modèles <span className="gold-shimmer-text">VideoBooth 360</span></>
            ) : (
              <>Nos Modèles <span className="gold-shimmer-text">PhotoBooth</span></>
            )}
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Découvrez nos designs personnalisables pour vos tirages photo
          </p>
        </div>
      </div>

      {/* Section Format Paysage - Défilement vers la gauche */}
      {paysageModels.length > 0 && (
        <section className="py-12 bg-black overflow-hidden" data-testid="section-paysage">
          <div className="max-w-7xl mx-auto px-6 mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-white mb-2">
              Format <span className="gold-shimmer-text">Paysage</span>
            </h2>
            <p className="text-white/50 text-center mb-8">Format carte postale (15×10 cm)</p>
          </div>
          
          {/* Mobile: One model at a time */}
          <div className="md:hidden">
            <MobileCarousel 
              models={paysageModels}
              CardComponent={PaysageCard}
              index={paysageIndex}
              setIndex={setPaysageIndex}
              onCardClick={setSelectedImage}
            />
          </div>
          
          {/* Desktop: Infinite carousel */}
          <div className="hidden md:block max-w-7xl mx-auto px-6">
            <InfiniteCarousel direction="left" speed={48} gradientColor="rgb(0, 0, 0)">
              {paysageModels.map((model, index) => (
                <PaysageCard 
                  key={model.id || `paysage-${index}`} 
                  model={model} 
                  onClick={() => setSelectedImage(model)}
                />
              ))}
            </InfiniteCarousel>
          </div>
        </section>
      )}

      {/* Section Format Portrait - Défilement vers la droite */}
      {portraitModels.length > 0 && (
        <section className="py-12 bg-black overflow-hidden" data-testid="section-portrait">
          <div className="max-w-7xl mx-auto px-6 mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-white mb-2">
              Format <span className="gold-shimmer-text">Portrait</span>
            </h2>
            <p className="text-white/50 text-center mb-8">Format vertical (10×15 cm)</p>
          </div>
          
          {/* Mobile: One model at a time */}
          <div className="md:hidden">
            <MobileCarousel 
              models={portraitModels}
              CardComponent={PortraitCard}
              index={portraitIndex}
              setIndex={setPortraitIndex}
              onCardClick={setSelectedImage}
            />
          </div>
          
          {/* Desktop: Infinite carousel */}
          <div className="hidden md:block max-w-7xl mx-auto px-6">
            <InfiniteCarousel direction="right" speed={40} gradientColor="rgb(0, 0, 0)">
              {portraitModels.map((model, index) => (
                <PortraitCard 
                  key={model.id || `portrait-${index}`} 
                  model={model} 
                  onClick={() => setSelectedImage(model)}
                />
              ))}
            </InfiniteCarousel>
          </div>
        </section>
      )}

      {/* Section Format Photomaton 5x15 (Bandelettes) - Défilement vers la gauche */}
      {bandeletteModels.length > 0 && (
        <section className="py-12 bg-black overflow-hidden" data-testid="section-bandelette">
          <div className="max-w-7xl mx-auto px-6 mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-white mb-2">
              Format <span className="gold-shimmer-text">Photomaton</span> 5×15
            </h2>
            <p className="text-white/50 text-center mb-8">Format bandelette classique (5×15 cm)</p>
          </div>
          
          {/* Mobile: One model at a time */}
          <div className="md:hidden">
            <MobileCarousel 
              models={bandeletteModels}
              CardComponent={BandeletteCard}
              index={bandeletteIndex}
              setIndex={setBandeletteIndex}
              onCardClick={setSelectedImage}
            />
          </div>
          
          {/* Desktop: Infinite carousel */}
          <div className="hidden md:block max-w-7xl mx-auto px-6">
            <InfiniteCarousel direction="left" speed={32} gradientColor="rgb(0, 0, 0)">
              {bandeletteModels.map((model, index) => (
                <BandeletteCard 
                  key={model.id || `bandelette-${index}`} 
                  model={model} 
                  onClick={() => setSelectedImage(model)}
                />
              ))}
            </InfiniteCarousel>
          </div>
        </section>
      )}

      {/* CTA Section - Gold gradient */}
      <section className="py-16" style={{ background: '#c29e5e' }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Nous réalisons vos modèles sur mesure
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Qu'ils soient verticaux, horizontaux ou bandelettes, communiquez vos instructions à nos graphistes.
          </p>
          <a 
            href="/contact" 
            className="inline-block font-semibold px-8 py-3 rounded-full hover:opacity-90 transition-colors shadow-lg"
            style={{ backgroundColor: '#E8D5A3', color: '#000000' }}
            data-testid="contact-cta"
          >
            Nous contacter
          </a>
        </div>
      </section>

      {/* Modal pour afficher l'image en grand */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
            data-testid="image-modal"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative max-w-4xl max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
                data-testid="close-modal-btn"
              >
                <FiX className="w-8 h-8" />
              </button>
              <img
                src={selectedImage.url}
                alt={selectedImage.name}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
              <p className="text-white text-center mt-4 text-lg font-medium">
                {selectedImage.name}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
